import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';

import PropTypes from 'prop-types';

import { t } from '~/helpers';

function GraficoArqueacaoAutomatica({
  data,
  intervalosJaArqueados,
  intervalosCapturados,
  intervaloAceito,
}) {
  const formatNumberBR = (value, suffix = '') => {
    if (value == null || Number.isNaN(Number(value))) {
      return suffix ? `- ${suffix}` : '-';
    }

    const formatted = Number(value).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });

    return suffix ? `${formatted} ${suffix}` : formatted;
  };

  const dataConvertida = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        alturaMm: Number(item.alturahistoricoarqautitem) * 10,
      })),
    [data]
  );

  const categoriasAltura = useMemo(
    () => dataConvertida.map((item) => item.alturaMm),
    [dataConvertida]
  );

  const estaNoIntervalo = (alturaMm, intervalos = []) =>
    intervalos.some(
      ({ alturainicial, alturafinal }) =>
        Number(alturaMm) >= Number(alturainicial) * 10 &&
        Number(alturaMm) <= Number(alturafinal) * 10
    );

  const yMax = useMemo(() => {
    const maiorVolume = Math.max(
      ...dataConvertida.map((item) =>
        Math.max(
          Number(item.volumehistoricoarqautitem || 0),
          Number(item.volumeoriginalhistoricoarqautitem || 0)
        )
      )
    );

    return maiorVolume * 1.05;
  }, [dataConvertida]);

  const {
    volumeAjustadoNormal,
    volumeAtual,
    areaIntervaloAceito,
    areaIntervalosCapturados,
    areaIntervalosJaArqueados,
  } = useMemo(() => {
    const ajustadoNormal = [];
    const atual = [];
    const aceito = [];
    const utilizados = [];
    const jaArqueados = [];

    dataConvertida.forEach((item) => {
      const { alturaMm } = item;

      ajustadoNormal.push(item.volumehistoricoarqautitem);
      atual.push(item.volumeoriginalhistoricoarqautitem);

      const estaAceito = intervaloAceito
        ? alturaMm >= Number(intervaloAceito.alturainicial) * 10 &&
          alturaMm <= Number(intervaloAceito.alturafinal) * 10
        : false;

      aceito.push(estaAceito ? yMax : null);
      utilizados.push(
        estaNoIntervalo(alturaMm, intervalosCapturados) ? yMax : null
      );
      jaArqueados.push(
        estaNoIntervalo(alturaMm, intervalosJaArqueados) ? yMax : null
      );
    });

    return {
      volumeAjustadoNormal: ajustadoNormal,
      volumeAtual: atual,
      areaIntervaloAceito: aceito,
      areaIntervalosCapturados: utilizados,
      areaIntervalosJaArqueados: jaArqueados,
    };
  }, [
    dataConvertida,
    intervalosCapturados,
    intervalosJaArqueados,
    intervaloAceito,
    yMax,
  ]);

  const series = [
    {
      name: t('s.operacao.arqueacaoautomatica.intervaloconfirmado'),
      data: areaIntervaloAceito,
      type: 'area',
    },
    {
      name: t('s.operacao.arqueacaoautomatica.intervalocapturado'),
      data: areaIntervalosCapturados,
      type: 'area',
    },
    {
      name: t('s.operacao.arqueacaoautomatica.intervalojaarqueado'),
      data: areaIntervalosJaArqueados,
      type: 'area',
    },
    {
      name: t('s.operacao.arqueacaoautomatica.novatabela'),
      data: volumeAjustadoNormal,
      type: 'line',
    },
    {
      name: t('s.operacao.arqueacaoautomatica.tabelaatual'),
      data: volumeAtual,
      type: 'line',
    },
  ];

  const colors = ['#06402b', '#ffc222', '#ff0000', '#4791db', '#f28c28'];

  return (
    <div style={{ height: '600px', width: '100%' }}>
      <Chart
        type="line"
        series={series}
        width="100%"
        height="100%"
        options={{
          colors,
          chart: {
            id: 'comparacao-volume-altura',
            stacked: false,
            toolbar: {
              show: false,
            },
            zoom: {
              enabled: true,
              type: 'xy',
              autoScaleYaxis: true,
            },
          },
          stroke: {
            curve: 'straight',
            width: [0, 0, 0, 2, 2],
          },
          fill: {
            opacity: [0.4, 0.4, 0.4, 1, 1],
          },
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            type: 'numeric',
            categories: categoriasAltura,
            axisTicks: {
              show: false,
            },
            labels: {
              formatter(val) {
                return formatNumberBR(val, 'mm');
              },
            },
          },
          yaxis: {
            min: 0,
            max: yMax,
            labels: {
              formatter(val) {
                return formatNumberBR(val, 'L');
              },
            },
          },
          markers: {
            size: [0, 0, 0, 0, 0],
          },
          tooltip: {
            shared: true,
            intersect: false,
            enabledOnSeries: [3, 4],
            marker: {
              show: false,
            },
            custom({ dataPointIndex }) {
              const categoriaAltura = dataConvertida[dataPointIndex]?.alturaMm;
              const volAjustado = volumeAjustadoNormal[dataPointIndex];
              const volAtualPonto = volumeAtual[dataPointIndex];

              const diferenca =
                volAtualPonto != null && volAjustado != null
                  ? volAtualPonto - volAjustado
                  : null;

              return `
                <div class="apexcharts-tooltip-box" style="padding: 8px 10px;">
                  <div class="apexcharts-tooltip-x-group" style="margin-bottom: 6px;">
                    <span class="apexcharts-tooltip-text-x-label">Altura: </span>
                    <span class="apexcharts-tooltip-text-x-value">${formatNumberBR(
                      categoriaAltura,
                      'mm'
                    )}</span>
                  </div>

                  <div class="apexcharts-tooltip-y-group" style="margin: 2px 0;">
                    <span
                      style="
                        background-color: ${colors[3]};
                        display: inline-block;
                        width: 10px;
                        height: 10px;
                        margin-right: 6px;
                        border-radius: 50%;
                      "
                    ></span>
                    <span>${t(
                      's.operacao.arqueacaoautomatica.volajustado'
                    )}: </span>
                    <span>${formatNumberBR(volAjustado, 'L')}</span>
                  </div>

                  <div class="apexcharts-tooltip-y-group" style="margin: 2px 0;">
                    <span
                      style="
                        background-color: ${colors[4]};
                        display: inline-block;
                        width: 10px;
                        height: 10px;
                        margin-right: 6px;
                        border-radius: 50%;
                      "
                    ></span>
                    <span>${t('s.operacao.recebimentonf.volatual')}: </span>
                    <span>${formatNumberBR(volAtualPonto, 'L')}</span>
                  </div>

                  ${
                    diferenca != null
                      ? `
                        <div style="border-top: 1px solid #eee; margin-top: 6px; padding-top: 6px;">
                          <span>${t(
                            's.relatorio.alteracaoencerrante.diferenca'
                          )}: </span>
                          <span>${formatNumberBR(diferenca, 'L')}</span>
                        </div>
                      `
                      : ''
                  }
                </div>
              `;
            },
          },
          legend: {
            show: true,
            position: 'top',
            horizontalAlign: 'right',
            floating: false,
            markers: {
              fillColors: [
                'rgba(6, 64, 43, 0.4)',
                'rgba(255, 194, 34, 0.4)',
                'rgba(255, 0, 0, 0.4)',
                '#4791db',
                '#f28c28',
              ],
            },
            onItemClick: {
              toggleDataSeries: true,
            },
            onItemHover: {
              highlightDataSeries: true,
            },
          },
        }}
      />
    </div>
  );
}

GraficoArqueacaoAutomatica.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      alturahistoricoarqautitem: PropTypes.number.isRequired,
      volumehistoricoarqautitem: PropTypes.number.isRequired,
      volumeoriginalhistoricoarqautitem: PropTypes.number.isRequired,
    })
  ).isRequired,
  intervalosJaArqueados: PropTypes.arrayOf(
    PropTypes.shape({
      alturainicial: PropTypes.number.isRequired,
      alturafinal: PropTypes.number.isRequired,
    })
  ),
  intervaloAceito: PropTypes.shape({
    alturainicial: PropTypes.number.isRequired,
    alturafinal: PropTypes.number.isRequired,
  }),
  intervalosCapturados: PropTypes.arrayOf(
    PropTypes.shape({
      alturainicial: PropTypes.number.isRequired,
      alturafinal: PropTypes.number.isRequired,
    })
  ).isRequired,
};

GraficoArqueacaoAutomatica.defaultProps = {
  intervaloAceito: null,
  intervalosJaArqueados: [],
};

export default GraficoArqueacaoAutomatica;
